import { AxiosResponse } from 'axios';
import Entity from './shared/entity';
import { Event } from './shared/eventstore-client/EventstoreClient';
import { ServiceClientWithEventstore } from './shared/service-client/ServiceClientWithEventstore';

export interface MessageResponse {
  message: string;
}

export interface Voucher extends Omit<Entity, 'id'> {
  code: string;
  state: VoucherState;
}

export interface GenericVoucher extends Voucher {
  batch: string;
}

export interface LinkedVoucher extends Voucher {
  secret: string;
  twinId: string;
}

export interface CreateLinkedVoucherRequest {
  code?: string;
  secret: string;
  twinId: string;
}

export interface CreateGenericVouchersRequest {
  amount: number;
}

export interface CreateGenericVouchersResponse {
  vouchers: GenericVoucher[];
}

export interface GetVouchersResponse<T> {
  vouchers: T[];
  continuationToken?: string;
}

export interface GetVouchersRequest {
  limit?: number;
  continuationToken?: string;
}

export interface LinkedClaimInput {
  code: string;
  secret: string;
}

export interface GenericClaimInput {
  code: string;
  twinId: string;
}

export enum VoucherState {
  CLAIMED = 'CLAIMED',
  READY = 'READY',
}

export type PERMISSIONS =
  | typeof DELETE
  | typeof GET_ALL
  | typeof GET
  | typeof CREATE
  | typeof GET_EVENTS_ANY
  | typeof GET_EVENTS_CREATED
  | typeof GET_EVENTS_CLAIMED
  | typeof GET_EVENTS_DELETED;

export const CREATE = 'VOUCHER:CREATE';
export const DELETE = 'VOUCHER:DELETE';
export const GET = 'VOUCHER:GET';
export const GET_ALL = 'VOUCHER:GET_ALL';
export const GET_EVENTS_ANY = 'VOUCHER:GET_EVENTS:ANY';
export const GET_EVENTS_CREATED = 'VOUCHER:GET_EVENTS:CREATED';
export const GET_EVENTS_CLAIMED = 'VOUCHER:GET_EVENTS:CLAIMED';
export const GET_EVENTS_DELETED = 'VOUCHER:GET_EVENTS:DELETED';

export const PERMISSION_NAMES = {
  CREATE,
  DELETE,
  GET,
  GET_ALL,
  GET_EVENTS_ANY,
  GET_EVENTS_CLAIMED,
  GET_EVENTS_CREATED,
  GET_EVENTS_DELETED,
};

export interface CreatedEvent extends Event {
  type: 'CREATED';
  data: Voucher;
}

export interface DeletedEvent extends Event {
  type: 'DELETED';
}

export interface ClaimedEvent extends Event {
  type: 'CLAIMED';
}

export type VoucherEvents = CreatedEvent | DeletedEvent | ClaimedEvent;

export class VoucherClient extends ServiceClientWithEventstore<VoucherEvents> {
  public async getAllLinkedVouchers(
    options?: GetVouchersRequest,
  ): Promise<AxiosResponse<GetVouchersResponse<LinkedVoucher>>> {
    return await this.httpClient.get('/linked-voucher/', { params: options, ...this.requestConfig });
  }

  public async getAllGenericVouchers(
    options?: GetVouchersRequest,
  ): Promise<AxiosResponse<GetVouchersResponse<GenericVoucher>>> {
    return await this.httpClient.get('/generic-voucher/', { params: options, ...this.requestConfig });
  }

  public async getLinkedVoucher(voucherCode: string): Promise<AxiosResponse<LinkedVoucher>> {
    return await this.httpClient.get(`/linked-voucher/${voucherCode}`, this.requestConfig);
  }

  public async getGenericVoucher(voucherCode: string): Promise<AxiosResponse<GenericVoucher>> {
    return await this.httpClient.get(`/generic-voucher/${voucherCode}`, this.requestConfig);
  }

  public async createLinkedVoucher(voucher: CreateLinkedVoucherRequest): Promise<AxiosResponse<LinkedVoucher>> {
    return await this.httpClient.post('/linked-voucher/', voucher, this.requestConfig);
  }

  public async createBatchOfGenericVouchers(
    input: CreateGenericVouchersRequest,
  ): Promise<AxiosResponse<CreateGenericVouchersResponse>> {
    return await this.httpClient.post('/generic-voucher/batch/', input, this.requestConfig);
  }

  public async claimLinkedVoucher(claim: LinkedClaimInput): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.post(
      `/linked-voucher/${claim.code}/claim`,
      {
        secret: claim.secret,
      },
      this.requestConfig,
    );
  }

  public async claimGenericVoucher(claim: GenericClaimInput): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.post(
      `/generic-voucher/${claim.code}/claim`,
      {
        twinId: claim.twinId,
      },
      this.requestConfig,
    );
  }

  public async deleteLinkedVoucher(voucherCode: string): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.delete(`/linked-voucher/${voucherCode}`, this.requestConfig);
  }

  public async deleteBatchOfGenericVouchers(batchId: string): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.delete(`/generic-voucher/batch/${batchId}`, this.requestConfig);
  }
}
